import React from 'react';
import { apiAction } from 'api/api';
import { useHistory } from 'react-router';
import { constants } from 'utils/Constant';
import { getTrendingCompanies } from 'api/urls';
import SearchContainer from 'components/wrapper/SearchContainer';
import SearchIcon from '@mui/icons-material/Search';

/* ─────────────────────────────────────────
   Design tokens (mirrors the Finalyze Redesign HTML spec)
───────────────────────────────────────── */
const tokens = {
  bg: '#F6F5F0',
  card: '#FFFFFF',
  soft: '#F0EEE6',
  line: '#E5E2D8',
  ink: '#232821',
  mut: '#6E766C',
  acc: '#2E9E7A',
  up: '#2C9C6F',
  down: '#D9614F',
  shadow: '0 1px 2px rgba(35,40,33,.04), 0 10px 28px rgba(35,40,33,.06)',
};

/* ─────────────────────────────────────────
   Inline styles
───────────────────────────────────────── */
const S = {
  root: {
    /* Cover the full viewport regardless of body/Bootstrap/paper-dashboard styles */
    position: 'fixed',
    inset: 0,
    overflowY: 'auto',
    background: tokens.bg,
    fontFamily: "'Instrument Sans', system-ui, sans-serif",
    zIndex: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  glow: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    /* rgba fallback instead of color-mix() for broader browser support */
    background:
      'radial-gradient(640px 320px at 50% 34%, rgba(46,158,122,0.07), transparent 72%)',
    zIndex: 0,
  },
  navbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 30px',
    position: 'relative',
    zIndex: 1,
  },
  logo: {
    fontFamily: "'Bricolage Grotesque', sans-serif",
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: '-0.02em',
    color: tokens.ink,
    userSelect: 'none',
  },
  logoAccent: { color: tokens.acc },
  themeBtn: {
    width: 36,
    height: 36,
    borderRadius: 999,
    border: `1px solid ${tokens.line}`,
    background: tokens.card,
    color: tokens.ink,
    fontSize: 15,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
  },
  hero: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '40px 40px',
    position: 'relative',
    zIndex: 1,
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: tokens.acc,
    margin: '0 0 14px',
  },
  headline: {
    margin: '0 0 14px',
    fontFamily: "'Bricolage Grotesque', sans-serif",
    fontSize: 'clamp(36px, 5vw, 54px)',
    fontWeight: 800,
    letterSpacing: '-0.025em',
    lineHeight: 1.05,
    color: tokens.ink,
  },
  headlineAccent: { color: tokens.acc },
  subheadline: {
    margin: '0 0 34px',
    maxWidth: 470,
    fontSize: 15.5,
    lineHeight: 1.55,
    color: tokens.mut,
  },
  searchWrap: {
    position: 'relative',
    width: '100%',
    maxWidth: 560,
  },
  searchIconWrap: {
    position: 'absolute',
    left: 20,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
  },
  trendingSection: { marginTop: 36 },
  trendingLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.13em',
    textTransform: 'uppercase',
    color: tokens.mut,
    margin: '0 0 12px',
  },
  trendingChips: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8,
  },
  chip: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    padding: '8px 15px',
    borderRadius: 999,
    border: `1px solid ${tokens.line}`,
    background: tokens.card,
    cursor: 'pointer',
    transition: 'transform 0.12s, box-shadow 0.12s',
  },
  chipSymbol: {
    fontSize: 12.5,
    fontWeight: 700,
    color: tokens.ink,
  },
};

/* ─────────────────────────────────────────
   Custom react-select styles for the search pill
───────────────────────────────────────── */
const searchStyles = {
  option: (provided, state) => ({
    ...provided,
    fontSize: 15,
    textAlign: 'left',
    cursor: 'pointer',
    color: state.isSelected ? tokens.acc : tokens.ink,
    backgroundColor: state.isSelected ? `${tokens.acc}18` : tokens.card,
  }),
  container: (base) => ({ ...base, width: '100%' }),
  control: (base) => ({
    ...base,
    height: 56,
    paddingLeft: 36,
    borderRadius: 999,
    border: `1.5px solid ${tokens.line}`,
    backgroundColor: tokens.card,
    boxShadow: tokens.shadow,
    '&:hover': { borderColor: tokens.acc },
    margin: 0,
    cursor: 'pointer',
  }),
  dropdownIndicator: (base) => ({ ...base, display: 'none' }),
  indicatorSeparator: (base) => ({ ...base, display: 'none' }),
  valueContainer: (base) => ({ ...base, padding: 0, paddingLeft: 4 }),
  placeholder: (base) => ({
    ...base,
    color: tokens.mut,
    fontSize: 15,
    fontFamily: "'Instrument Sans', system-ui, sans-serif",
  }),
  input: (base) => ({
    ...base,
    fontSize: 15,
    color: tokens.ink,
    fontFamily: "'Instrument Sans', system-ui, sans-serif",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 14,
    boxShadow: tokens.shadow,
    border: `1px solid ${tokens.line}`,
    overflow: 'hidden',
  }),
};

/* ─────────────────────────────────────────
   Search / Landing page component
───────────────────────────────────────── */
const Search = () => {
  const history = useHistory();
  const [trendingData, setTrendingData] = React.useState([]);

  /* Inject Google Fonts + reset body styles that Bootstrap/paper-dashboard override */
  React.useEffect(() => {
    /* Google Fonts */
    const id = 'finalyze-home-gfonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;700;800&family=Instrument+Sans:wght@400;500;600;700&display=swap';
      document.head.appendChild(link);
    }

    /* Force body to be neutral so our fixed root div shows cleanly */
    const prevBg = document.body.style.background;
    const prevMargin = document.body.style.margin;
    const prevPadding = document.body.style.padding;
    const prevOverflow = document.body.style.overflow;
    document.body.style.background = tokens.bg;
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';

    return () => {
      /* Restore body styles when navigating away */
      document.body.style.background = prevBg;
      document.body.style.margin = prevMargin;
      document.body.style.padding = prevPadding;
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  /* Fetch trending companies */
  React.useEffect(() => {
    const getApiResults = async () => {
      const res = await apiAction({ url: getTrendingCompanies() });
      setTrendingData(res ? res : []);
    };
    getApiResults();
  }, []);

  /* Navigate to the company dashboard */
  const onClickedHandler = (name, symbol) => {
    history.push(
      `${constants.finalyze}/?company__name=` +
      encodeURIComponent(name) +
      '&company__symbol=' +
      encodeURIComponent(symbol)
    );
  };

  const changeColor = (item) => {
    if (item.change_percent !== undefined) {
      return parseFloat(item.change_percent) >= 0 ? tokens.up : tokens.down;
    }
    return tokens.mut;
  };

  const formatChange = (item) => {
    if (item.change_percent !== undefined) {
      const v = parseFloat(item.change_percent);
      return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
    }
    return null;
  };

  return (
    <div style={S.root}>
      {/* Scoped micro-animation styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;700;800&family=Instrument+Sans:wght@400;500;600;700&display=swap');
        .home-chip:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(35,40,33,.10); }
        .home-theme-btn:hover { background: #F0EEE6 !important; }
      `}</style>

      {/* Background glow */}
      <div style={S.glow} />

      {/* ── Navbar ── */}
      <nav style={S.navbar}>
        <div style={S.logo}>
          finalyze<span style={S.logoAccent}>.</span>
        </div>
        <button
          className="home-theme-btn"
          style={S.themeBtn}
          title="Toggle light / dark"
          aria-label="Toggle theme"
        >
          ☾
        </button>
      </nav>

      {/* ── Hero ── */}
      <main style={S.hero}>
        <p style={S.eyebrow}>Equity research, simplified</p>

        <h1 style={S.headline}>
          The data for investing{' '}
          <span style={S.headlineAccent}>right</span>.
        </h1>

        <p style={S.subheadline}>
          Fundamentals, peer context and fair-value models for every NSE-listed
          company — in one scroll.
        </p>

        {/* Search pill */}
        <div style={S.searchWrap}>
          <span style={S.searchIconWrap}>
            <SearchIcon sx={{ fontSize: 22, color: tokens.mut }} style={{ width: 22, height: 22, display: 'block' }} />
          </span>
          <SearchContainer
            placeHolder={
              <span style={{ color: tokens.mut, display: 'block', textAlign: 'left', marginLeft: 10 }}>
                Type a company name to search
              </span>
            }
            customStyles={searchStyles}
            icon={null}
          />
        </div>

        {/* Trending today */}
        {trendingData.length > 0 && (
          <div style={S.trendingSection}>
            <p style={S.trendingLabel}>Trending today</p>
            <div style={S.trendingChips}>
              {trendingData.map((item, idx) => {
                const change = formatChange(item);
                const color = changeColor(item);
                return (
                  <div
                    key={idx}
                    className="home-chip"
                    style={S.chip}
                    onClick={() => onClickedHandler(item.name, item.symbol)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      e.key === 'Enter' && onClickedHandler(item.name, item.symbol)
                    }
                  >
                    <span style={S.chipSymbol}>{item.symbol}</span>
                    {change && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {change}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Search;
